import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import Layout from '../components/Layout';
import SplitView from '../components/SplitView';
import PropertyCard from '../components/PropertyCard';
import PropertyModal from '../PropertyModal';
import ComparisonTray from '../components/ComparisonTray';
import { DEFAULT_CITY, DEFAULT_STATE } from '../config/locations';
import { API_URL } from '../config/api';
import { trackSearchEvent, trackConversionEvent } from '../utils/analytics';

const SearchPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [hoveredPropertyId, setHoveredPropertyId] = useState(null);

    // On first visit (no location params), write defaults to URL so
    // "first visit" and "user cleared" become distinguishable states.
    useEffect(() => {
        if (!searchParams.has('city') && !searchParams.has('zip_code')) {
            const newParams = new URLSearchParams(searchParams);
            newParams.set('city', DEFAULT_CITY);
            newParams.set('state', DEFAULT_STATE);
            setSearchParams(newParams, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Track search page visit
    useEffect(() => {
        trackConversionEvent('search');
    }, []);

    const filters = {
        city: searchParams.get('city') || '',
        state: searchParams.get('state') || '',
        zip_code: searchParams.get('zip_code') || '',
        min_roi: searchParams.get('min_roi') || '',
        max_roi: searchParams.get('max_roi') || '',
        min_price: searchParams.get('min_price') || '',
        max_price: searchParams.get('max_price') || '',
        min_beds: searchParams.get('min_beds') || '',
        min_baths: searchParams.get('min_baths') || '',
        property_types: searchParams.get('property_types') || '',
        investment_types: searchParams.get('investment_types') || '',
        max_days_on_market: searchParams.get('max_days_on_market') || '',
        min_year_built: searchParams.get('min_year_built') || '',
        feature_keywords: searchParams.get('feature_keywords') || '',
        sort_by: searchParams.get('sort_by') || 'newest',
    };

    // If zip_code is set, don't also filter by city/state (zip is more specific)
    if (filters.zip_code) {
        filters.city = '';
        filters.state = '';
    }

    const hasLocation = !!(filters.city || filters.zip_code);

    // Track search when location becomes available
    useEffect(() => {
        if (hasLocation) {
            trackSearchEvent({
                location: filters.city || filters.zip_code,
                priceMin: filters.min_price,
                priceMax: filters.max_price,
                bedrooms: filters.min_beds,
                bathrooms: filters.min_baths,
                type: 'property',
            });
        }
    }, [hasLocation]);

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading,
    } = useInfiniteQuery({
        queryKey: ['properties', filters],
        queryFn: async ({ pageParam = 1 }) => {
            const params = new URLSearchParams();
            params.append('page', pageParam);
            params.append('page_size', 20);

            Object.entries(filters).forEach(([key, value]) => {
                if (value) {
                    if (key === 'property_types' || key === 'investment_types') {
                        value.split(',').filter(Boolean).forEach(v => {
                            params.append(key, v);
                        });
                    } else {
                        params.append(key, value);
                    }
                }
            });

            const response = await fetch(`${API_URL}/properties?${params.toString()}`);
            if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
            return response.json();
        },
        getNextPageParam: (lastPage, allPages) => {
            return lastPage.items.length === 20 ? allPages.length + 1 : undefined;
        },
        initialPageParam: 1,
        staleTime: 1000 * 60 * 5,
        enabled: hasLocation,
    });

    const properties = data ? data.pages.flatMap(page => page.items) : [];
    const totalCount = data?.pages?.[0]?.total || 0;

    const { data: mapData } = useQuery({
        queryKey: ['mapMarkers', filters],
        queryFn: async () => {
            const params = new URLSearchParams();
            Object.entries(filters).forEach(([key, value]) => {
                if (value && key !== 'sort_by') {
                    if (key === 'property_types' || key === 'investment_types') {
                        value.split(',').filter(Boolean).forEach(v => params.append(key, v));
                    } else {
                        params.append(key, value);
                    }
                }
            });
            const response = await fetch(`${API_URL}/properties/map-markers?${params.toString()}`);
            if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
            return response.json();
        },
        staleTime: 1000 * 60 * 5,
        enabled: hasLocation,
    });
    const mapMarkers = mapData?.markers || [];

    const mapCenter = useMemo(() => {
        const geoProps = mapMarkers.filter(p => p.latitude && p.longitude);
        if (geoProps.length === 0) return null;
        const avgLat = geoProps.reduce((sum, p) => sum + p.latitude, 0) / geoProps.length;
        const avgLon = geoProps.reduce((sum, p) => sum + p.longitude, 0) / geoProps.length;
        return [avgLat, avgLon];
    }, [mapMarkers]);

    const handleFilterChange = (arg1, arg2) => {
        const newParams = new URLSearchParams(searchParams);

        if (typeof arg1 === 'string') {
            const name = arg1;
            const value = arg2;
            if (value) {
                newParams.set(name, value);
            } else {
                newParams.delete(name);
            }
        } else {
            const updates = arg1;
            Object.entries(updates).forEach(([key, value]) => {
                if (value) {
                    newParams.set(key, value);
                } else {
                    newParams.delete(key);
                }
            });
        }

        // Track search filter changes
        const updatedFilters = {
            location: newParams.get('city') || newParams.get('zip_code'),
            priceMin: newParams.get('min_price'),
            priceMax: newParams.get('max_price'),
            bedrooms: newParams.get('min_beds'),
            bathrooms: newParams.get('min_baths'),
            type: 'property',
        };

        // Only track if we have location data (meaningful search)
        if (updatedFilters.location) {
            trackSearchEvent(updatedFilters);
        }

        setSearchParams(newParams);
    };

    return (
        <Layout>
            <SplitView
                properties={properties}
                totalCount={totalCount}
                filters={filters}
                onFilterChange={handleFilterChange}
                onCardClick={(property) => setSelectedProperty(property)}
                hoveredPropertyId={hoveredPropertyId}
                setHoveredPropertyId={setHoveredPropertyId}
                PropertyCardComponent={PropertyCard}
                isLoading={isLoading}
                onLoadMore={() => fetchNextPage()}
                hasMore={!!hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
                mapCenter={mapCenter}
                hasLocation={hasLocation}
                mapMarkers={mapMarkers}
            />

            {selectedProperty && (
                <PropertyModal property={selectedProperty} onClose={() => setSelectedProperty(null)} />
            )}

            <ComparisonTray />
        </Layout>
    );
};

export default SearchPage;
